const rowKey = (product) => `${product.id}-${product.select_type.id}`

/**
 * What kind of row this is. The same product+price can be in the basket several
 * times with different meanings (bought, discounted to 0.01 by a promotion,
 * gift, sampler), and those rows must never be merged into one another or share
 * a render key — a merged row loses its markers and gets sent back to
 * sync-basket as an ordinary purchase.
 *
 * @param {object} row basket row or an order product from the response
 * @returns {'main'|'free'|'discounted'|'present'|'sampler'|'sale'}
 */
export const basketRowKind = (row) => {
  if (!row) return 'main'
  if (row.isFree) return 'free'
  if (row.isDiscountedUnit) return 'discounted'
  if (row.isPresent) return 'present'
  if (row.isSamplerPresent) return 'sampler'
  if (row.isAddedFromSale) return 'sale'

  return 'main'
}

/**
 * Rows the backend adds on top of the basket by itself: free samplers and gifts
 * of regular sales. The customer never put them there, and the backend recreates
 * them on every sync from what we send — so sending them back is never needed,
 * and is harmful: the payload has no flag that marks a row as a gift, so the
 * backend reads it as an ordinary purchase and charges full price.
 */
const isBackendExtra = (product) => {
  if (product.isSamplerPresent || product.isAddedFromSale) return true

  // Fallback for rows that reached the basket before the sampler flag was
  // carried through (localStorage survives deploys): a sampler priced at 0.01
  // per unit is a free sampler everywhere in this codebase.
  const count = product.select_count || 0
  return !!(product.type === 'sampler' && count > 0 && product.allProductSum / count === 0.01)
}

/**
 * Whether a basket row is one of the units the customer chose themselves — the
 * basis for both the sync-basket payload and the quantity counters.
 *
 * `is_present` needs care. Under a "third free" promotion the backend splits the
 * customer's own quantity into a paid row and a 0.01 row flagged `is_present`;
 * that row is NOT an extra, it is one of their units. Dropping it from the
 * payload lowers the count below the promotion threshold, so the promotion never
 * re-applies and the basket collapses back (3 → 2 → 3 → 2). The backend marks
 * exactly those rows with `is_discounted_unit`, so no guessing is needed: a
 * present without that flag is a genuine gift and stays out of the payload.
 *
 * @param {object} product basket row
 * @param {Array<object>} basket the whole basket, for the legacy pairing check
 * @returns {boolean}
 */
export const countsAsOwnUnit = (product, basket) => {
  if (!product || product.isFree) return false

  // The backend's own marker beats every guess below: a discounted unit is the
  // customer's even when it is priced at 0.01 like a free sampler.
  if (product.isDiscountedUnit) return true

  if (isBackendExtra(product)) return false
  if (!product.isPresent) return true

  // Flag is there and false — a present the customer never added.
  if (product.isDiscountedUnit !== undefined) return false

  // Legacy fallback for rows stored before `is_discounted_unit` existed
  // (localStorage survives deploys): treat a present as the customer's own unit
  // only when the same product+price is also in the basket as a paid row. Every
  // row that goes through a sync carries the flag, so this path dies out on its
  // own after the first sync of a stale basket.
  const key = rowKey(product)
  return (basket || []).some(
    (other) =>
      other !== product &&
      !other.isFree &&
      !other.isPresent &&
      !isBackendExtra(other) &&
      rowKey(other) === key,
  )
}

/**
 * The basket as the customer should see it: one row per position.
 *
 * Under a "third free" promotion the backend splits the customer's own quantity
 * into a paid row and a single unit discounted to 0.01 (`is_discounted_unit`).
 * Both rows are their units, so drawing both shows the same bottle twice — three
 * bottles read as four, and price × quantity stops matching the row sum.
 * Merging them back into one row keeps the row self-consistent and makes the row
 * sums add up to the backend's `total_amount`.
 *
 * The merged row is a copy: it keeps the paid row's `hash`, so the counter and
 * the delete button still resolve to a real basket row, while the store keeps
 * both rows — the sync-basket payload is built from the store, not from here.
 *
 * Gifts, samplers and sale items keep their own rows: those are extras the
 * customer never added and they are not part of the position's quantity.
 *
 * @param {Array<object>} basket
 * @returns {Array<object>} rows to render
 */
export const basketDisplayRows = (basket) => {
  const byPosition = new Map()
  const display = []

  ;(basket || []).forEach((row) => {
    const kind = basketRowKind(row)

    if (kind !== 'main' && kind !== 'discounted') {
      display.push(row)
      return
    }

    const key = rowKey(row)
    const merged = byPosition.get(key)

    if (!merged) {
      // Rendered as an ordinary position: the discounted unit is the customer's,
      // so the row stays editable even when the promotion discounted the whole
      // position and no paid row came back at all.
      const own = { ...row, isPresent: false, isDiscountedUnit: false }
      byPosition.set(key, own)
      display.push(own)
      return
    }

    merged.select_count += row.select_count || 0
    merged.allProductSum = (merged.allProductSum || 0) + (row.allProductSum || 0)
    merged.discount = (merged.discount || 0) + (row.discount || 0)

    // The paid row carries the position's identity and its undiscounted price.
    if (kind === 'main') {
      merged.hash = row.hash
      merged.pricePer1 = row.pricePer1
      merged.discountPer1 = row.discountPer1
    }
  })

  return display
}

export const buildBasketPayloadProducts = (basket, giftPayloadItems = []) => {
  const totals = new Map()

  basket
    .filter((product) => countsAsOwnUnit(product, basket))
    .forEach((product) => {
      const key = rowKey(product)
      const existing = totals.get(key)

      if (existing) {
        existing.count += product.select_count
        return
      }

      totals.set(key, {
        id: product.id,
        product_price_id: product.select_type.id,
        count: product.select_count,
        is_present: false,
      })
    })

  const selectedGifts = basket
    .filter((product) => product.isFree)
    .map((product) => ({
      product_price_id: product.select_type.id,
      count: product.select_count,
      is_free: true,
      sale_id: product.saleId ?? null,
    }))

  const gifts = selectedGifts.length ? selectedGifts : giftPayloadItems || []

  return [...Array.from(totals.values()), ...gifts]
}
